import { z } from "zod";
import { MarketSymbolSchema } from "../branded";
import {
  CandleSchema,
  FearGreedSchema,
  MarketDaySchema,
  OrderbookSchema,
  PriceSchema,
} from "../entities";
import { CandleIntervalSchema, MarketSchema } from "../enums";
import { EpochMsSchema } from "../primitives";

/** `GET /api/market/prices?symbols=` (comma-separated, max 200). */
export const MarketPricesQuerySchema = z.object({
  symbols: z.string().min(1), // comma-separated symbol list
});
export type MarketPricesQuery = z.infer<typeof MarketPricesQuerySchema>;

export const MarketPricesResponseSchema = z.object({
  prices: z.array(PriceSchema),
});
export type MarketPricesResponse = z.infer<typeof MarketPricesResponseSchema>;

/** `GET /api/market/candles`. */
export const MarketCandlesQuerySchema = z.object({
  symbol: MarketSymbolSchema,
  interval: CandleIntervalSchema.default("1d"),
  count: z.coerce.number().int().min(1).max(200).optional(),
  before: z.string().optional(), // ISO8601 cursor
  adjusted: z.stringbool().optional(),
});
export type MarketCandlesQuery = z.infer<typeof MarketCandlesQuerySchema>;

export const MarketCandlesResponseSchema = z.object({
  symbol: MarketSymbolSchema,
  interval: CandleIntervalSchema,
  candles: z.array(CandleSchema),
  nextBefore: z.string().nullable(),
});
export type MarketCandlesResponse = z.infer<typeof MarketCandlesResponseSchema>;

/** `GET /api/market/orderbook`. */
export const MarketOrderbookQuerySchema = z.object({
  symbol: MarketSymbolSchema,
});
export type MarketOrderbookQuery = z.infer<typeof MarketOrderbookQuerySchema>;

export const MarketOrderbookResponseSchema = OrderbookSchema;
export type MarketOrderbookResponse = z.infer<typeof MarketOrderbookResponseSchema>;

/** `GET /api/market/calendar/:market`. */
export const MarketCalendarParamSchema = z.object({ market: MarketSchema });
export type MarketCalendarParam = z.infer<typeof MarketCalendarParamSchema>;

export const MarketCalendarResponseSchema = z.object({
  market: MarketSchema,
  days: z.array(MarketDaySchema),
});
export type MarketCalendarResponse = z.infer<typeof MarketCalendarResponseSchema>;

/** `GET /api/market/fgi` — CNN Fear & Greed (Worker-proxied + cached). */
export const MarketFgiResponseSchema = FearGreedSchema;
export type MarketFgiResponse = z.infer<typeof MarketFgiResponseSchema>;

/**
 * Display-only market-overview stats (indices / sectors / news). These are
 * plain `z.number()`, NOT the branded {@link DecimalSchema}: the branded Decimal
 * is reserved for money arithmetic done in `@richrich/core/money`, and none of
 * these values ever feed an order, a budget, or a P&L calculation — they are a
 * read-only dashboard sourced from third-party feeds (Yahoo/CNN/RSS) and are
 * fine to carry as JS floats over the wire.
 */

/** One market index snapshot with an intraday sparkline. */
export const IndexQuoteSchema = z.object({
  symbol: z.string(),
  name: z.string(),
  price: z.number(),
  previousClose: z.number(),
  changePercent: z.number(),
  currency: z.string(),
  sparkline: z.array(z.number()), // downsampled intraday closes (≤ 40 points)
  marketTime: EpochMsSchema.nullable(),
});
export type IndexQuote = z.infer<typeof IndexQuoteSchema>;

/** `GET /api/market/indices` — headline indices (S&P 500, Nasdaq, …). */
export const MarketIndicesResponseSchema = z.object({
  asOf: EpochMsSchema,
  indices: z.array(IndexQuoteSchema),
});
export type MarketIndicesResponse = z.infer<typeof MarketIndicesResponseSchema>;

/** One SPDR sector-ETF snapshot (no sparkline — heatmap is single-value). */
export const SectorQuoteSchema = z.object({
  symbol: z.string(),
  name: z.string(),
  price: z.number(),
  previousClose: z.number(),
  changePercent: z.number(),
});
export type SectorQuote = z.infer<typeof SectorQuoteSchema>;

/** `GET /api/market/sectors` — the 11 SPDR sector ETFs (heatmap). */
export const MarketSectorsResponseSchema = z.object({
  asOf: EpochMsSchema,
  sectors: z.array(SectorQuoteSchema),
});
export type MarketSectorsResponse = z.infer<typeof MarketSectorsResponseSchema>;

/** `GET /api/market/fgi/history?days=` — CNN Fear & Greed daily series. */
export const MarketFgiHistoryQuerySchema = z.object({
  days: z.coerce.number().int().min(7).max(365).default(90),
});
export type MarketFgiHistoryQuery = z.infer<typeof MarketFgiHistoryQuerySchema>;

export const MarketFgiHistoryResponseSchema = z.object({
  points: z.array(
    z.object({
      date: z.string(), // YYYY-MM-DD (UTC)
      score: z.number(),
    }),
  ),
});
export type MarketFgiHistoryResponse = z.infer<typeof MarketFgiHistoryResponseSchema>;

/** The 11 SPDR sector ETFs a treemap stock rolls up to (mirrors `SectorEtf`). */
export const StockSectorSchema = z.enum([
  "XLK",
  "XLF",
  "XLE",
  "XLV",
  "XLI",
  "XLY",
  "XLP",
  "XLU",
  "XLB",
  "XLRE",
  "XLC",
]);
export type StockSector = z.infer<typeof StockSectorSchema>;

/** One treemap tile: a single stock, sized by `weight`, colored by `changePercent`. */
export const StockQuoteSchema = z.object({
  symbol: z.string(),
  name: z.string(),
  sector: StockSectorSchema,
  weight: z.number(), // cap-proportional % of the top-200 — drives tile area.
  price: z.number().nullable(), // last trade; null when the bulk quote missed it.
  changePercent: z.number().nullable(), // vs the baseline prev close; null when no baseline/price.
});
export type StockQuote = z.infer<typeof StockQuoteSchema>;

/**
 * `GET /api/market/stocks` — the per-stock treemap (종목 히트맵). Like the sector
 * heatmap this is display-only (plain `z.number()`, see the block above), but its
 * `price`/`changePercent` come from Toss (behind the `trading` gate), not Yahoo,
 * and `changePercent` is computed against a stored previous-close baseline —
 * `baselineDate` is the trading day that baseline was taken at (`null` = no
 * baseline yet, so every `changePercent` is null).
 */
export const MarketStocksResponseSchema = z.object({
  asOf: EpochMsSchema,
  baselineDate: z.string().nullable(), // YYYY-MM-DD of the prev-close baseline, null = none yet.
  stocks: z.array(StockQuoteSchema),
});
export type MarketStocksResponse = z.infer<typeof MarketStocksResponseSchema>;

/** `GET /api/market/news` — merged market headlines from public RSS feeds. */
export const MarketNewsResponseSchema = z.object({
  asOf: EpochMsSchema,
  items: z.array(
    z.object({
      title: z.string(),
      link: z.string(),
      source: z.string(), // "CNBC" | "MarketWatch"
      publishedAt: EpochMsSchema.nullable(),
    }),
  ),
});
export type MarketNewsResponse = z.infer<typeof MarketNewsResponseSchema>;
